import { useEffect } from 'react';

interface OrganizationData {
  name: string;
  url: string;
  logo?: string;
  description?: string;
  sameAs?: string[];
  contactPoint?: {
    contactType: string;
    email: string;
  };
}

interface WebsiteData {
  url: string;
  name: string;
  description?: string;
  potentialAction?: {
    '@type': string;
    target: string;
    'query-input': string;
  };
}

interface ArticleData {
  headline: string;
  description: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: {
    '@type': string;
    name: string;
  };
  publisher?: {
    '@type': string;
    name: string;
    logo?: {
      '@type': string;
      url: string;
    };
  };
}

export const StructuredData: React.FC<{
  organization?: OrganizationData;
  website?: WebsiteData;
  article?: ArticleData;
}> = ({ organization, website, article }) => {
  useEffect(() => {
    const scripts: Array<{ type: string; children: string }> = [];

    // Organization 结构化数据
    if (organization) {
      scripts.push({
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: organization.name,
          url: organization.url,
          ...(organization.logo && { logo: organization.logo }),
          ...(organization.description && { description: organization.description }),
          ...(organization.sameAs && { sameAs: organization.sameAs }),
          ...(organization.contactPoint && {
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: organization.contactPoint.contactType,
              email: organization.contactPoint.email,
            },
          }),
        }),
      });
    }

    // Website 结构化数据
    if (website) {
      scripts.push({
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: website.url,
          name: website.name,
          ...(website.description && { description: website.description }),
          ...(website.potentialAction && {
            potentialAction: {
              '@type': website.potentialAction['@type'],
              target: website.potentialAction.target,
              'query-input': website.potentialAction['query-input'],
            },
          }),
        }),
      });
    }

    // Article 结构化数据
    if (article) {
      scripts.push({
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.headline,
          description: article.description,
          ...(article.image && { image: article.image }),
          ...(article.datePublished && { datePublished: article.datePublished }),
          ...(article.dateModified && { dateModified: article.dateModified }),
          ...(article.author && {
            author: {
              '@type': article.author['@type'],
              name: article.author.name,
            },
          }),
          ...(article.publisher && {
            publisher: {
              '@type': article.publisher['@type'],
              name: article.publisher.name,
              ...(article.publisher.logo && {
                logo: {
                  '@type': article.publisher.logo['@type'],
                  url: article.publisher.logo.url,
                },
              }),
            },
          }),
        }),
      });
    }

    // 添加所有结构化数据脚本到页面
    scripts.forEach((scriptData) => {
      const script = document.createElement('script');
      script.type = scriptData.type;
      script.innerHTML = scriptData.children;
      script.id = `structured-data-${Date.now()}-${Math.random()}`;
      document.head.appendChild(script);
    });

    // 清理函数
    return () => {
      scripts.forEach((_, index) => {
        const script = document.getElementById(`structured-data-${index}`);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, [organization, website, article]);

  return null;
};

// 默认的组织和网站数据
export const defaultOrganizationData: OrganizationData = {
  name: 'Titron (TRN)',
  url: 'https://titron.org',
  logo: '/images/Logo.jpg',
  description: 'Titron是为Web3构建的隐私保护和数据主权基石协议，通过零知识证明和同态加密技术，让个人和组织能够在数据可验证的前提下安全地共享和利用数据价值。',
  sameAs: [
    'https://x.com/Titron_TRN',
    'https://github.com/annaufdga',
    'https://discord.com/invite/titron',
  ],
  contactPoint: {
    contactType: 'Customer Service',
    email: 'contact@titron.org',
  },
};

export const defaultWebsiteData: WebsiteData = {
  url: 'https://titron.org',
  name: 'Titron - Web3隐私保护协议',
  description: '为Web3构建隐私保护和数据主权基石协议',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://titron.org/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};


